import { Tenant } from "albertine-shared-web";
import type { DecodedIdToken } from "firebase-admin/auth";
import { type ZodError, z } from "zod";

// Re-exporting Tenant as it is often needed in the context of permissions.
export { Tenant };

export const AgentScope = z.enum(["dev", "admin", "admin:updateAgent", "agent"]);

export type AgentScope = z.infer<typeof AgentScope>;

export const AllowedTenant = Tenant.or(z.literal("*"));
export type AllowedTenant = z.infer<typeof AllowedTenant>;

export const MemberClaims = z.object({
    member: z.literal(true),
    tenantId: Tenant,

    /**
     * @deprecated The `roles` field is ONLY used by the OpenSearch authentication.
     * It should not be used for any other purpose.
     */
    roles: z.literal("member"),
});

export type MemberClaims = z.infer<typeof MemberClaims>;

export const AgentClaims = z.object({
    agent: z.literal(true),
    agentScopes: z.array(AgentScope),
    tenants: z.array(AllowedTenant),

    /**
     * @deprecated The `roles` field is ONLY used by the OpenSearch authentication.
     * It should not be used for any other purpose.
     */
    roles: z.literal("agent"),
});

export type AgentClaims = z.infer<typeof AgentClaims>;

export const Claims = z.union([MemberClaims, AgentClaims]);
export type Claims = z.infer<typeof Claims>;

/**
 * Use to augment logging information with the relevant claims from the decoded ID token.
 */
export const loggerDataFromClaims = (
    authUserId: string,
    appUserId: string,
    parsedClaims: ParseClaimsResult,
) => {
    switch (parsedClaims.type) {
        case "agent":
            return {
                uid: authUserId,
                agent: appUserId,
                agentScopes: parsedClaims.claims.agentScopes,
                tenants: parsedClaims.claims.tenants,
            };
        case "member":
            return {
                uid: authUserId,
                member: appUserId,
                tenant: parsedClaims.claims.tenantId,
            };
        case "unknown":
            return { uid: authUserId };
        case "error":
            return {};
    }
};

export type ParseClaimsResult =
    | { type: "agent"; claims: AgentClaims }
    | { type: "member"; claims: MemberClaims }
    | { type: "unknown"; claims: Claims }
    | { type: "error"; error: ZodError };

/**
 * Parse the claims from a decoded ID token.
 */
export const parseClaims = (idToken: DecodedIdToken): ParseClaimsResult => {
    const parsedClaims = Claims.safeParse(idToken);
    if (!parsedClaims.success) {
        return { type: "error", error: parsedClaims.error };
    }

    const claims = parsedClaims.data;

    if ("agent" in claims && claims.agent === true) {
        return { type: "agent", claims };
    }

    if ("member" in claims && claims.member === true) {
        return { type: "member", claims };
    }

    return { type: "unknown", claims };
};

/**
 * Check whether the scope is allowed by the token scopes. The scopes are
 * namespaced, that is, "dev:system-tests" is allowed if the token has the
 * "dev" scope.
 *
 * @param scope The scope to check.
 * @param tokenScopes The scopes of the token that will be validated against.
 * @returns Whether the scope is allowed.
 */
export function scopeAllowed(scope: AgentScope, tokenScopes: AgentScope[]) {
    if (tokenScopes.includes(scope)) {
        return true;
    }

    return tokenScopes.includes(scope.split(":")[0] as AgentScope);
}

/**
 * Check whether the tenant is allowed by the token tenants.
 *
 * @param tenant The tenant to check.
 * @param tokenTenants The tenants of the token that will be validated against.
 * @returns Whether the tenant is allowed.
 */
export function tenantAllowed(tenant: AllowedTenant, tokenTenants: AllowedTenant[]) {
    if (tenant === "*") {
        return tokenTenants.includes("*");
    }

    return tokenTenants.includes(tenant) || tokenTenants.includes("*");
}

/**
 * Check whether the claims are allowed to perform the operation defined by
 * the scope and tenant.
 *
 * @returns True if and only if the agent has the required permissions.
 */
export function checkAgentClaims(
    agentClaims: Pick<AgentClaims, "tenants" | "agentScopes">,
    operation: { tenant: Tenant | null; scope: AgentScope | null },
): { ok: true } | { ok: false; failed: "tenant" | "scope" } {
    const { tenant, scope } = operation;
    const { tenants, agentScopes } = agentClaims;

    if (tenant && !tenantAllowed(tenant, tenants)) {
        return { ok: false, failed: "tenant" };
    }

    if (scope && !scopeAllowed(scope, agentScopes)) {
        return { ok: false, failed: "scope" };
    }

    return { ok: true };
}
